/**
 * Prototyper message helpers — shared between history hydration, pill visibility,
 * and build-stream start decisions.
 */

import type { Message, PrototyperBuildTimeline } from '../types/conversation.types';

/** Fields used to detect a prototyper turn on an assistant message. */
export type PrototyperMessageFields = Pick<
  Message,
  | 'hasPrototype'
  | 'prototypeReady'
  | 'prototyper_demo_link_id'
  | 'buildTimeline'
  | 'buildStatus'
  | 'prototypeSnapshotId'
>;

function hasTruthyId(value: string | number | null | undefined): boolean {
  return value != null && value !== '';
}

/**
 * True when the assistant message represents a prototyper turn (live or from history).
 */
export function messageHasPrototyperArtifact(message: PrototyperMessageFields): boolean {
  if (message.hasPrototype === true) return true;
  if (message.prototypeReady === true) return true;
  if (hasTruthyId(message.prototyper_demo_link_id)) return true;
  if (message.buildTimeline != null) return true;
  if (hasTruthyId(message.prototypeSnapshotId ?? undefined)) return true;
  return false;
}

/**
 * True when a plan turn already has persisted build output — skip auto build-stream.
 */
export function planMessageHasPersistedBuild(message: PrototyperMessageFields): boolean {
  if (message.buildTimeline != null) return true;
  if (message.buildStatus === 'success') return true;
  if (hasTruthyId(message.prototypeSnapshotId ?? undefined)) return true;
  return false;
}

/** Derive `hasPrototype` for history hydration from persisted interaction fields. */
export function deriveHasPrototypeFromFields(fields: PrototyperMessageFields): boolean {
  return messageHasPrototyperArtifact(fields);
}

export type PrototyperBuildSessionFields = {
  buildTimeline?: PrototyperBuildTimeline | null;
  buildStatus?: string;
  prototypeDisplayName?: string | null;
  prototypeVersionIndex?: number | null;
  prototypeSnapshotId?: string | null;
};

/**
 * Read build-session fields from an llm-interaction row (flat or nested join).
 */
export function extractPrototyperBuildSessionFromInteraction(
  interaction: Record<string, unknown>,
): PrototyperBuildSessionFields {
  const session = (interaction.prototyper_build_session ??
    interaction.prototyperBuildSession) as Record<string, unknown> | undefined;

  return {
    buildTimeline: (interaction.build_timeline ??
      interaction.buildTimeline ??
      session?.build_timeline ??
      session?.buildTimeline) as PrototyperBuildTimeline | undefined,
    buildStatus: (interaction.build_status ??
      interaction.buildStatus ??
      session?.build_status ??
      session?.buildStatus) as string | undefined,
    prototypeDisplayName: (interaction.prototype_display_name ??
      interaction.prototypeDisplayName ??
      session?.prototype_display_name ??
      session?.prototypeDisplayName) as string | null | undefined,
    prototypeVersionIndex: (interaction.prototype_version_index ??
      interaction.prototypeVersionIndex ??
      session?.prototype_version_index ??
      session?.prototypeVersionIndex) as number | null | undefined,
    prototypeSnapshotId: (interaction.prototype_snapshot_id ??
      interaction.prototypeSnapshotId ??
      session?.prototype_snapshot_id ??
      session?.prototypeSnapshotId) as string | null | undefined,
  };
}
